/**
 * Buffers slideshow song audio ahead of playback — the audio counterpart to
 * slideshowImagePreloader. Given a resolved stream URL, it creates a detached
 * HTMLAudioElement with `preload='auto'` so the browser fetches the media before the
 * song's window is reached on the timeline.
 *
 * Crucially, the element that buffers a song is also the element useSlideshowAudio
 * plays: at the song's boundary the hook just unmutes and calls play() on the
 * already-loaded element, so there is no second network request and no cold-fetch
 * stall. (An earlier approach used a throwaway buffering element separate from a
 * single playback element, but browsers don't reliably share buffered media between
 * two elements — and these stream responses carry no Cache-Control — so the playback
 * element re-fetched. Reusing the buffered element avoids that entirely.)
 *
 * Elements are created muted so a buffering song can never sound before the hook
 * activates it. Buffering reuses the exact URL playback will use (via the shared
 * stream-URL cache in useSlideshowAudio): a separately re-signed URL would waste the
 * download.
 */

export type PreloadedAudioElements = Map<string, HTMLAudioElement>;

/**
 * Starts buffering a song's audio if it isn't already, and returns the element for it.
 * Keyed by song id so each song buffers exactly once across re-renders; returns the
 * existing element when already buffering, or null when no url is available.
 */
export function bufferSongAudio(
    elements: PreloadedAudioElements,
    songId: string,
    url: string
): HTMLAudioElement | null {
    const existing = elements.get(songId);

    if (existing) {
        return existing;
    }

    if (!url) {
        return null;
    }

    const audio = new Audio();

    // `preload='auto'` asks the browser to fetch the media data now, not just metadata.
    audio.preload = 'auto';
    // Muted while buffering so it can't sound before the hook activates it for playback.
    audio.muted = true;
    audio.src = url;
    // Some browsers won't begin fetching until load() is called after src is assigned.
    audio.load();

    elements.set(songId, audio);

    return audio;
}

/**
 * Stops and releases a single buffered element (aborting any in-flight fetch) and drops
 * it from the map. A no-op when that song isn't buffered.
 */
function releaseSong(elements: PreloadedAudioElements, songId: string): void {
    const audio = elements.get(songId);

    if (!audio) {
        return;
    }

    audio.pause();
    audio.src = '';
    // Abort any pending network request and release buffered data.
    audio.load();
    elements.delete(songId);
}

/**
 * Releases every buffered element whose song id is NOT in `keepSongIds`, leaving the
 * rest (and their in-progress buffering / current playback) untouched. Used when the
 * timeline's song set changes so songs that are still present aren't needlessly torn
 * down and re-downloaded.
 */
export function releaseBufferedAudioExcept(
    elements: PreloadedAudioElements,
    keepSongIds: ReadonlySet<string>
): void {
    Array.from(elements.keys()).forEach((songId) => {
        if (!keepSongIds.has(songId)) {
            releaseSong(elements, songId);
        }
    });
}

/**
 * Tears down every buffered element — stops in-flight fetches and releases the media
 * so detached audio elements don't leak. Clears the map so the same instance can be
 * reused for a new set of songs.
 */
export function releaseBufferedAudio(elements: PreloadedAudioElements): void {
    elements.forEach((audio) => {
        audio.pause();
        audio.src = '';
        // Abort any pending network request and release buffered data.
        audio.load();
    });

    elements.clear();
}
